import { createContext, useContext, useMemo, useState } from "react";
import { mapOrderEventDTOToOrder, Order } from "@/schemas/order";
import { useOrderContext } from "./order-context";
import { usePostGetAllOrders } from "@/api/pizza-checker/orders/orders";
import { OrdersData } from "@/api/custom-schema";

interface SelectedOrderContextType {
  selectedOrder: Order | null;
  selectedOrderId: string | null;
  setSelectedOrderId: (orderId: string | null) => void;
  loading: boolean;
}

interface SelectedOrderProviderProps {
  children: React.ReactNode;
}

const SelectedOrderContext = createContext<
  SelectedOrderContextType | undefined
>(undefined);

export const SelectedOrderProvider = ({
  children,
}: SelectedOrderProviderProps) => {
  const { orders } = useOrderContext();
  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);
  const { data, isLoading: loading } = usePostGetAllOrders<OrdersData>(
    {
      skip: 0,
      limit: 1,
      filters: selectedOrderId ? { "data.id": selectedOrderId } : undefined,
    },
    {
      query: {
        enabled: selectedOrderId != null,
        refetchOnMount: "always",
        refetchOnWindowFocus: true,
      },
    },
  );

  const selectedOrder = useMemo(() => {
    if (!selectedOrderId) return null;
    if (data?.orders && data.orders[0]) {
      return mapOrderEventDTOToOrder(data.orders[0]);
    }
    const foundOrder = orders?.find((order) => order.id === selectedOrderId);
    if (foundOrder) return foundOrder;
    return null;
  }, [orders, data, selectedOrderId]);

  return (
    <SelectedOrderContext.Provider
      value={useMemo(
        () => ({
          selectedOrder,
          selectedOrderId,
          setSelectedOrderId,
          loading,
        }),
        [selectedOrder, selectedOrderId, loading],
      )}
    >
      {children}
    </SelectedOrderContext.Provider>
  );
};

export const useSelectedOrder = () => {
  const context = useContext(SelectedOrderContext);
  if (!context) {
    throw new Error(
      "useSelectedOrder must be used within a SelectedOrderProvider",
    );
  }
  return context;
};
